import React, { useContext, useEffect } from 'react';
import { ReviewsRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import {
  arrayOf as arrayType,
  extend,
  params,
  QueryContext,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  FeatureBasedRecommendationsImporterVite as FeatureBasedRecommendationsImporter
} from '@thd-olt-component-react/feature-based-recommendations';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryCarousel, MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { PprMetadata } from '@thd-olt-component-react/metadata';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import {
  RatingsAndReviewsDesktopImporterVite as RatingsAndReviewsDesktopImporter,
  RatingsAndReviewsMobileImporterVite as RatingsAndReviewsMobileImporter
} from '@thd-olt-component-react/ratings-and-reviews';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { DynamicRecsWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton } from '@thd-olt-component-react/thd-sharebelt';

import DynamicRecsWrapperImporter from '../components/DynamicRecsWrapperImporter';
import ProductDetailsReviewsRatings from '../components/ProductDetailsReviewsRatings';
import ProductReviewsHeader from '../components/ProductReviewsHeader';
import ViewDetailsLink from '../components/ViewDetailsLink';

const RECS_REQUEST_KEY = 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee';

export const ProductReviews = () => {
  declareContexts([ExperienceContext, QueryContext]);
  const qctx = useContext(QueryContext);
  const { channel } = useContext(ExperienceContext);
  const { itemId, page } = useParams();
  const { pathname, search } = useLocation();
  let emtLinksPath;
  if (page) {
    emtLinksPath = pathname;
  } else if (pathname.endsWith('/')) {
    emtLinksPath = `${pathname}1`;
  } else {
    emtLinksPath = `${pathname}/1`;
  }

  const isMobile = channel === 'mobile';
  const pageNumber = page === undefined ? '1' : page;
  const seoPageNumber = parseInt(pageNumber, 10);

  const { data, loading } = useDataModel('product', { variables: { itemId } });
  const { product } = data || {};

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
          'paginated-product-reviews.page-load',
          { product, pageType: 'reviews' }
        );
      }
    }
  }, []);

  return (
    <div className="sui-bg-primary">
      <div className="sui-max-w-screen-2xl sui-m-auto">
        <ErrorBoundary name="redirector">
          <ReviewsRedirector
            pathWithQueryParams={pathname + search}
            itemId={itemId}
          />
        </ErrorBoundary>

        <ErrorBoundary name="metadata">
          <PprMetadata itemId={itemId} page={page} />
        </ErrorBoundary>

        <div className="sui-px-3 2xl:sui-px-0 sui-mt-2">
          <ErrorBoundary name="breadcrumbs">
            <Breadcrumbs itemId={itemId} />
          </ErrorBoundary>
        </div>

        {isMobile && (
          <ErrorBoundary name="product-reviews-header">
            <div className="sui-px-3 2xl:sui-px-0 sui-mt-6 sui-mb-4">
              <ProductReviewsHeader itemId={itemId} />
            </div>
          </ErrorBoundary>
        )}

        {!isMobile && (
          <ErrorBoundary name="product-info-bar">
            <div className="sui-px-3 2xl:sui-px-0 sui-mt-6">
              <ProductInfoBar itemId={itemId} />
            </div>
          </ErrorBoundary>

        )}

        <div className="sui-grid sui-grid-cols-1 lg:sui-grid-cols-12 sui-w-full">
          <div className="sui-col-span-6 sui-px-3 2xl:sui-px-0">
            {isMobile && (
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.BrandCollection />
                  <ProductDetailsReviewsRatings itemId={itemId} isMobile={isMobile} />
                </ProductDetails>
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="media-gallery">
                <MediaGalleryDesktop itemId={itemId} mainImageFetchPriority="high" />
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="sharebelt-desktop">
                <Sharebelt horizontal itemId={itemId}>
                  <ShareBeltShareButton email />
                  <ShareBeltPrint />
                </Sharebelt>
              </ErrorBoundary>
            )}

          </div>

          {!isMobile && (
            <div className="lg:sui-col-start-8 lg:sui-col-span-4 sui-px-3 lg:sui-px-0">
              <ErrorBoundary name="product-reviews-header">
                <ProductReviewsHeader itemId={itemId} />
              </ErrorBoundary>
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.BrandCollection />
                  <ProductDetailsReviewsRatings itemId={itemId} isMobile={isMobile} />
                </ProductDetails>
              </ErrorBoundary>
              <ErrorBoundary name="salient-points">
                <SalientPoints itemId={itemId} max={3} />
              </ErrorBoundary>
              <div className="sui-mt-3">
                <ViewDetailsLink itemId={itemId} trackingParam="?emt_ppr_vfpd" />
              </div>
            </div>
          )}

          {isMobile && (
            <>
              <ErrorBoundary name="media-gallery">
                <div className="sui-col-span-12">
                  <MediaGalleryCarousel itemId={itemId} firstItemFetchPriority="high" />
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="product-info-bar">
                <div className="sui-col-span-12 sui-px-3 2xl:sui-px-0">
                  <ProductInfoBar itemId={itemId} />
                </div>
              </ErrorBoundary>
            </>
          )}
        </div>

        {isMobile && (
          <div className="sui-px-3 2xl:sui-px-0">
            <div className="sui-mt-3">
              <ViewDetailsLink itemId={itemId} trackingParam="?emt_ppr_vfpd" />
            </div>
          </div>
        )}

        <ErrorBoundary name="ratings-and-reviews">
          {isMobile ? (
            <RatingsAndReviewsMobileImporter
              itemId={itemId}
              seoPageNumber={seoPageNumber}
              hideOverlay
              qctx={qctx}
              hydrator={{
                className: 'sui-px-3 2xl:sui-px-0 sui-my-4 empty:sui-p-0 empty:sui-m-0',
              }}
              dynamic={{
                pageType: 'productReviews'
              }}
            />
          ) : (
            <RatingsAndReviewsDesktopImporter
              itemId={itemId}
              seoPageNumber={seoPageNumber}
              hideOverlay
              qctx={qctx}
              hydrator={{
                className: 'sui-px-3 2xl:sui-px-0 sui-my-4 empty:sui-p-0 empty:sui-m-0',
              }}
              dynamic={{
                pageType: 'productReviews'
              }}
            />
          )}
        </ErrorBoundary>

        <ErrorBoundary name="feature-based-recommendations">
          <FeatureBasedRecommendationsImporter
            hideOnMobile
            itemId={itemId}
            qctx={qctx}
            hydrator={{
              className: 'sui-mb-4 empty:sui-mb-0',
              scrollBuffer: 750,
              id: 'fbr-hydrator'
            }}
            dynamic={{
              pageType: 'productReviews',
            }}
          />
        </ErrorBoundary>

        <DynamicComponent pageType="productReviews">
          <ErrorBoundary name="dynamic-recs">
            <DynamicRecsWrapper
              anchorId={itemId}
              errorBoundary
              hideATC
              hydrator={{ id: 'dynamic-recs-hydrator', scrollBuffer: 750 }}
              impressionData={{
                component: 'DynamicRecs',
                type: 'product'
              }}
              requestKey={RECS_REQUEST_KEY}
              schemaName="pip_alternatives"
            />
          </ErrorBoundary>
        </DynamicComponent>

        <ErrorBoundary name="emt-links">
          <EmtLinks
            dynamic={{ pageType: 'productReviews' }}
            hydrator={{ scrollBuffer: 750, className: 'sui-px-3 2xl:sui-px-0 sui-mt-4 empty:sui-mt-0' }}
            path={emtLinksPath}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

ProductReviews.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired() }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        canonicalUrl: stringType(),
        itemId: stringType(),
        modelNumber: stringType(),
        omsThdSku: stringType(),
        productLabel: stringType(),
        storeSkuNumber: stringType(),
        specialOrderSku: stringType(),
        upcGtin13: stringType()
      }),
      media: shapeType({
        image: shapeType({
          url: stringType()
        }).client(),
        images: arrayType(shapeType({
          url: stringType()
        }))
      }),
      taxonomy: shapeType({
        breadCrumbs: arrayType(shapeType({
          label: stringType(),
          url: stringType()
        }))
      })
    })
  },
  Breadcrumbs,
  DynamicRecsWrapperImporter,
  DynamicRecsWrapper,
  EmtLinks,
  FeatureBasedRecommendationsImporter,
  MediaGalleryCarousel,
  MediaGalleryDesktop,
  ProductDetails,
  ProductDetailsReviewsRatings,
  ProductReviewsHeader,
  RatingsAndReviewsDesktopImporter,
  RatingsAndReviewsMobileImporter,
  ReviewsRedirector,
  SalientPoints,
  ViewDetailsLink,
  PprMetadata
);